<template>
  <div>
    <div v-if="formLoading">
      <GridLoading></GridLoading>
    </div>
    <div>
      <spreadsheet ref="spreadsheet">
        <spreadsheet-sheet :name="'Style Upload Sheet'" :rows="jsondata" :columns="columns"> </spreadsheet-sheet>
      </spreadsheet>
    </div>
  </div>
</template>
<script lang="ts">
import { Spreadsheet, SpreadsheetSheet } from "@progress/kendo-spreadsheet-vue-wrapper";
import { TenantDto, DataImportDto } from "@/api/model";
import { DataImportApi, UserAdminSettingApi } from "@/api";
import { PermissionGuard } from "@/core/plugins/permissions";
import GridLoading from "./GridLoading.vue";
import { defineComponent } from "vue";
import { useToast } from "vue-toastification";
import plausible from "@/core/plausible";

export default defineComponent({
  name: "SpreadSheet",
  components: { Spreadsheet, SpreadsheetSheet, GridLoading },
  setup() {
    const dataImportApi = new DataImportApi()!;
    const userAdminSettingApi = new UserAdminSettingApi()!;
    return {
      dataImportApi,
      userAdminSettingApi
    };
  },
  data() {
    return {
      toast: useToast(),
      jsondataExcelSheet: null as any,
      formLoading: false,
      errorCell: [] as any,
      successMsg: this.$t("spreadSheets.dataSavedSuccesfully") as any,
      success: this.$t("spreadSheets.success") as any,
      errorMsg: this.$t("spreadSheets.thereAreSomeErrors") as any,
      error: this.$t("spreadSheets.error") as any,
      formatErrorVal: this.$t("spreadSheets.formatError") as any,
      show: false,
      seamLengthUnit: "" as any,
      tktTexValue: "" as any,
      defaultAllowanceVal: "" as any,
      columns: [
        {
          width: 100
        },
        {
          width: 155
        },
        {
          width: 120
        },
        {
          width: 100
        },
        {
          width: 150
        },
        {
          width: 150
        },
        {
          width: 100
        },
        {
          width: 100
        },
        {
          width: 150
        },
        {
          width: 150
        },
        {
          width: 150
        },
        {
          width: 100
        },
        {
          width: 100
        },
        {
          width: 100
        },
        {
          width: 160
        },
        {
          width: 100
        },
        {
          width: 100
        }
      ]
    };
  },
  props: {
    bus: {
      type: null as any,
      required: true
    },
    jsondata: {
      type: Array as () => any[],
      required: true
    },
    importType: {
      type: null as any,
      required: true
    },
    isError: {
      type: Boolean
    }
  },
  computed: {
    canModifyPermissions(): boolean {
      return PermissionGuard("AbpTenantManagement.Tenants.ManageFeatures");
    }
  },
  async mounted(): Promise<void> {
    let spreadsheet = (this.$refs["spreadsheet"] as any).kendoWidget();
    spreadsheet.element.css("height", `${spreadsheet.height}`);
    spreadsheet.element.css("width", "100vw");
    spreadsheet.resize();
    this.gettingAdminData();
    this.bus.on("submit", this.submit);
  },
  methods: {
    submit(): void {
      this.onSave();
    },
    async getEntity(): Promise<TenantDto> {
      return {};
    },
    async onSave(): Promise<void> {
      this.formLoading = true;
      let stringigyJSon = JSON.stringify(this.jsondata);
      let parsedJson = JSON.parse(stringigyJSon);
      let spreadsheet = (this.$refs["spreadsheet"] as any).kendoWidget();
      let data = spreadsheet.toJSON();
      let dataSheet = data["sheets"];
      let dataFirstIndex = dataSheet?.[0];
      let dataSheetRows = dataFirstIndex["rows"];
      let excelCellVal = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R"];
      let activeCell: any;
      let cellNum: any;
      this.jsondataExcelSheet = Object.assign(dataSheetRows);
      const dataImportDto: DataImportDto = {
        importType: this.importType,
        name: parsedJson?.[0].defaultName,
        rows: this.jsondataExcelSheet,
        seamLengthUnit: this.seamLengthUnit,
        tktTexVal: sessionStorage.getItem("tktTexValue")
      } as DataImportDto;
      let resColorFlag = false;
      await this.dataImportApi
        .apiImportPost(dataImportDto)
        .then(async (res: any) => {
          if (res.data.error) {
            resColorFlag = true;
            this.toast.error(this.formatErrorVal, {
              toastClassName: "custom-error-toast",
              timeout: 5000
            });
            return;
          }
          for (let rowVal of res.data.rows) {
            for (let cellVal of rowVal.cells) {
              cellNum = excelCellVal[cellVal.index] + (rowVal.index + 1);
              activeCell = spreadsheet.activeSheet().range(cellNum);
              if (
                this.errorCell.length > 0 &&
                Array.isArray(this.errorCell) &&
                this.errorCell.includes(cellNum) &&
                cellVal.color == null
              ) {
                activeCell.comment(null);
                activeCell.background(cellVal.color);
                this.errorCell = this.filterCell(cellNum);
              } else if (cellVal.color != null) {
                resColorFlag = true;
                activeCell.comment(cellVal.comment);
                activeCell.background(cellVal.color);
                if (!this.errorCell.includes(cellNum)) {
                  this.errorCell.push(cellNum);
                }
              }
            }
          }
          if (resColorFlag) {
            this.toast.error(this.errorMsg, {
              toastClassName: "custom-error-toast",
              timeout: 5000
            });
          } else {
            this.toast.success(this.successMsg, {
              toastClassName: "custom-success-toast",
              timeout: 5000
            });
          }
        })
        .then(async () => {
          if (!resColorFlag) {
            location.reload();
          }
        })
        .catch(e => {
          this.toast.error(e.errorMsg, {
            toastClassName: "custom-error-toast",
            timeout: 5000
          });
        })
        .finally(() => {
          this.formLoading = false;
        });
      plausible.trackEvent("AnalysisCreated", { props: { TenantName: localStorage.getItem("TenantName") ?? "" } });
    },
    filterCell(cellNum: any) {
      return this.errorCell.filter((item: any) => item != cellNum);
    },
    async gettingAdminData(): Promise<void> {
      await this.userAdminSettingApi.apiUserAdminSettingGet().then(async res => {
        for (let adminData of res.data.items ?? []) {
          if (adminData.field == "TktTex") {
            this.tktTexValue = adminData.value as any;
          } else if (adminData.field == "SeamLengthUnit") {
            this.seamLengthUnit = adminData.value as any;
          } else if (adminData.field == "DefaultAllowance") {
            this.defaultAllowanceVal = adminData.value as any;
          }
        }
      });
    }
  }
});
</script>
<style scoped>
/* kendo-spreadsheet style */
.b-toaster {
  z-index: 999999 !important;
}
.loader {
  height: 100% !important;
  width: 100% !important;
  position: absolute !important;
  top: 2rem !important;
}
</style>
